import {
  Box,
  Button,
  // eslint-disable-next-line no-restricted-syntax -- consumed transitively by libs/public-pages (excluded from the DS migration); must stay on raw MUI.
  IconButton,
  Typography,
  FormLabel,
  FormHelperText,
  Dialog,
  DialogContent,
  DialogActions
} from '@mui/material';
import { AttachFile, Image as ImageIcon, Close as CloseIcon } from '@mui/icons-material';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';

const convertTitleToTitleArray = (title: string | string[]) => {
  if (Array.isArray(title)) {
    return title.length === 0 ? ['צרף קובץ'] : title;
  }
  return title.split(',');
};

export const InputFile = ({
  title = 'צרף קובץ',
  id,
  file,
  setFile,
  multiple,
  helperText,
  isImage = false,
  isRequired = false,
  fieldTitle,
  disabled,
  initialPreviewUrls = [],
  openDialogOnImageClick = false,
  disablePreview = false
}: {
  title?: string | string[];
  id: string;
  file?: File | string | undefined | (File | string)[];
  setFile: (files: File | (File | string)[] | string | undefined) => void;
  multiple?: boolean;
  helperText?: string;
  isImage?: boolean;
  isRequired?: boolean;
  fieldTitle?: string;
  disabled?: boolean;
  initialPreviewUrls?: { thumbnail: string; image: string }[];
  openDialogOnImageClick?: boolean;
  disablePreview?: boolean;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [dialogImageUrl, setDialogImageUrl] = useState<string>('');
  const previousFileRef = useRef<File | string | undefined | (File | string)[]>();
  const previousPreviewUrlsRef = useRef<string[]>([]);

  // Memoize the title array to prevent unnecessary re-renders
  const titleArray = useMemo(() => convertTitleToTitleArray(title), [title]);

  // Memoize the initial preview URLs to prevent unnecessary re-renders
  const memoizedInitialPreviewUrls = useMemo(
    () => initialPreviewUrls.map(urlObj => urlObj.thumbnail),
    [initialPreviewUrls]
  );

  const handleImageClick = useCallback(
    (imageUrl: string) => {
      const imageUrlToShow = initialPreviewUrls.find(urlObj => urlObj.thumbnail === imageUrl)?.image || imageUrl;
      setDialogImageUrl(imageUrlToShow);
      setPreviewDialogOpen(true);
    },
    [initialPreviewUrls]
  );

  const handleCloseDialog = useCallback(() => {
    setPreviewDialogOpen(false);
    setDialogImageUrl('');
  }, []);

  // Initialize preview URLs when component mounts or when file changes
  useEffect(() => {
    // Only update if file actually changed to prevent infinite loops
    if (file !== previousFileRef.current) {
      previousFileRef.current = file;

      if (file) {
        if (isImage && !disablePreview) {
          const getPreviewUrls = (file: File | (File | string)[] | string) => {
            if (Array.isArray(file)) {
              return file.map(f => {
                if (f instanceof File) {
                  return URL.createObjectURL(f);
                } else if (typeof f === 'string') {
                  return f;
                }
                return '';
              });
            } else if (file instanceof File) {
              return [URL.createObjectURL(file)];
            } else if (typeof file === 'string') {
              return [file];
            }
            return [];
          };
          const newPreviewUrls = getPreviewUrls(file);
          // Only update state if the URLs actually changed
          if (JSON.stringify(newPreviewUrls) !== JSON.stringify(previousPreviewUrlsRef.current)) {
            previousPreviewUrlsRef.current = newPreviewUrls;
            setPreviewUrls(newPreviewUrls);
          }
        }
      } else {
        // Only update state if the URLs actually changed
        if (JSON.stringify(memoizedInitialPreviewUrls) !== JSON.stringify(previousPreviewUrlsRef.current)) {
          previousPreviewUrlsRef.current = memoizedInitialPreviewUrls;
          setPreviewUrls(memoizedInitialPreviewUrls);
        }
      }
    }
  }, [file, isImage, disablePreview, memoizedInitialPreviewUrls]);

  // Cleanup function for object URLs
  useEffect(() => {
    return () => {
      previewUrls.forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFiles = event.target.files;

      if (!newFiles?.length) {
        return;
      }

      if (multiple) {
        const fileList = Array.from(newFiles);
        const currentFiles = Array.isArray(file) ? file : [file];
        const allFiles = [...currentFiles.filter(f => f !== undefined), ...fileList];

        if (allFiles.length > 10) {
          const selectedFiles = allFiles.slice(0, 10);
          const remainingCount = allFiles.length - 10;
          setFile(selectedFiles as (File | string)[]);

          alert(
            `נבחרו ${allFiles.length} קבצים. הועלו 10 הקבצים הראשונים, ${remainingCount} קבצים לא הועלו עקב מגבלת המערכת`
          );
        } else {
          setFile(allFiles as (File | string)[]);
        }
      } else {
        setFile(newFiles[0]);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [multiple, file]
  );

  const handleDeleteFile = useCallback(
    (index?: number) => {
      if (index === undefined) {
        setFile(undefined);
        setPreviewUrls([]);
        return;
      }
      if (multiple && Array.isArray(file)) {
        const newFiles = [...file];
        newFiles.splice(index, 1);
        setFile(newFiles as File[]);

        if (isImage && !disablePreview) {
          URL.revokeObjectURL(previewUrls[index]);
          const newPreviewUrls = [...previewUrls];
          newPreviewUrls.splice(index, 1);
          setPreviewUrls(newPreviewUrls);
        }
      } else {
        setFile(undefined);
        setPreviewUrls([]);
      }
    },
    [multiple, file, setFile, isImage, disablePreview, previewUrls]
  );

  // Get the title to show
  const getTitleToShow = () => {
    if (isRequired) {
      const requiredTitle = Array.isArray(title) ? title : [title];
      return requiredTitle.map(t => `${t} *`);
    }

    return titleArray.length === 1
      ? titleArray[0]
      : titleArray.map(t => (
          <span key={t}>
            {t}
            <br />
          </span>
        ));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {fieldTitle && (
        <Box sx={{ mb: -2 }}>
          <FormLabel required={isRequired}>{fieldTitle}</FormLabel>
          {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <input
          ref={fileInputRef}
          type="file"
          id={id}
          multiple={multiple}
          accept={isImage ? 'image/*' : undefined}
          style={{ display: 'none' }}
          onChange={handleFileChange}
          disabled={disabled}
        />
        <IconButton
          disabled={disabled}
          aria-label={isImage ? 'העלאת תמונה' : 'צירוף קובץ'}
          onClick={() => fileInputRef.current?.click()}
        >
          {isImage ? <ImageIcon /> : <AttachFile />}
        </IconButton>
        {(isImage || titleArray.length === 1) && (
          <Typography
            variant="body1"
            component="span"
            sx={{
              color: disabled ? 'text.disabled' : 'inherit',
              cursor: disabled ? 'not-allowed' : 'pointer',
              ml: 1
            }}
            onClick={() => {
              if (!disabled) fileInputRef.current?.click();
            }}
          >
            {getTitleToShow()}
          </Typography>
        )}
      </Box>

      {!fieldTitle && helperText && (
        <Typography variant="caption" color="textSecondary">
          {helperText}
        </Typography>
      )}

      {(previewUrls.length > 0 || titleArray.length > 1) && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: previewUrls.length === 1 ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
            justifyItems: previewUrls.length === 1 ? 'center' : 'stretch'
          }}
        >
          {previewUrls.length > 0
            ? previewUrls.map((url, index) => (
                <Box
                  key={url}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    maxWidth: previewUrls.length === 1 ? '400px' : '100%'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: 200,
                      position: 'relative',
                      overflow: 'hidden',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      ...(openDialogOnImageClick && {
                        cursor: 'pointer',
                        '&:hover': {
                          opacity: 0.8,
                          transition: 'opacity 0.2s'
                        }
                      })
                    }}
                    {...(openDialogOnImageClick && {
                      onClick: () => handleImageClick(url)
                    })}
                  >
                    <img
                      src={url}
                      alt={`תמונה ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  </Box>
                  <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteFile(index)}>
                    הסר
                  </Button>
                </Box>
              ))
            : titleArray.length > 1 &&
              titleArray.map((title, index) => (
                <Box key={title} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    key={title}
                    variant="body1"
                    component="span"
                    sx={{ color: disabled ? 'text.disabled' : 'inherit' }}
                  >
                    {title}
                  </Typography>
                  <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteFile(index)}>
                    הסר
                  </Button>
                </Box>
              ))}
        </Box>
      )}
      {file && (!Array.isArray(file) || file.length > 0) && (!isImage || disablePreview) && titleArray.length === 1 && (
        <Button variant="outlined" color="primary" size="small" onClick={() => handleDeleteFile()}>
          מחק
        </Button>
      )}

      {/* Image Preview Dialog */}
      {openDialogOnImageClick && (
        <Dialog
          open={previewDialogOpen}
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              maxHeight: '90vh',
              maxWidth: '90vw',
              display: 'flex',
              flexDirection: 'column',
              margin: { xs: 1, sm: 2 }
            }
          }}
        >
          <DialogActions sx={{ p: 1, justifyContent: 'flex-end' }}>
            <IconButton size="small" aria-label="סגור" onClick={handleCloseDialog}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogActions>
          <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, minHeight: 0 }}>
            {dialogImageUrl && (
              <img
                src={dialogImageUrl}
                alt="תמונה בגודל מלא"
                style={{
                  maxWidth: '80vw',
                  maxHeight: '70vh',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default InputFile;
